import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { X, RefreshCw, Plus, Minus, Zap, History, ShieldAlert } from "lucide-react";

const SP_CHOICES = [20, 30, 40, 50, 60, 70, 80, 90, 100];
const SP_MIN = 0.5;
const SP_MAX = 100;
const isValidSp = (s) => /^\d+(\.\d)?$/.test(String(s).trim()) && Number(s) >= SP_MIN && Number(s) <= SP_MAX;
const r2 = (x) => Math.round(Number(x || 0) * 100) / 100;

/** Admin + Super-Admin. Add / subtract a fixed SP delta on a member with
 *  audit trail. Preserves locked closings by routing the correction to
 *  the current open period.
 */
export default function SpAdjustModal({ member, onClose, onSaved }) {
  const [direction, setDirection] = useState("add");     // "add" | "subtract"
  const [amountStr, setAmountStr] = useState("50");
  const amount = Number(amountStr);
  const amountOk = isValidSp(amountStr);
  const [reason, setReason] = useState("");
  const [targetPeriod, setTargetPeriod] = useState("");   // blank = current open
  const [busy, setBusy] = useState(false);
  const [audit, setAudit] = useState([]);
  const [showAudit, setShowAudit] = useState(false);

  const delta = useMemo(() => (direction === "add" ? amount : -amount), [direction, amount]);
  const preview = useMemo(() => {
    const before = {
      self_sp: Number(member?.self_sp || 0),
      total_sp: Number(member?.total_sp || 0),
    };
    return {
      before,
      after: { self_sp: r2(before.self_sp + delta), total_sp: before.total_sp /* team leg-side unchanged by self-adjust to self */ },
    };
  }, [member, delta]);

  const loadAudit = async () => {
    try {
      const { data } = await api.get(`/admin/users/${member.user_code}/audit?limit=50`);
      setAudit(data?.sp_adjustments || []);
      setShowAudit(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load audit");
    }
  };

  useEffect(() => { setAmountStr("50"); setDirection("add"); setReason(""); setTargetPeriod(""); setShowAudit(false); }, [member?.user_code]);

  const submit = async () => {
    if (reason.trim().length < 10) { toast.error("Reason must be at least 10 characters"); return; }
    if (!amountOk) { toast.error(`SP must be ${SP_MIN}–${SP_MAX} with at most 1 decimal (e.g. 15.5)`); return; }
    if (!window.confirm(
      `${direction === "add" ? "ADD" : "SUBTRACT"} ${amount} SP on ${member.user_code} — ${member.name}?\n\n` +
      `Before → After (self_sp): ${preview.before.self_sp} → ${preview.after.self_sp}\n` +
      `Reason: ${reason.trim()}\n${targetPeriod ? `Target period: ${targetPeriod}\n` : ""}` +
      `\nThis is permanently audit-logged. Locked closings will NOT be recalculated — ` +
      `if the target period is already locked, the correction will be recorded against the current open period with a link back.`
    )) return;
    setBusy(true);
    try {
      const body = { delta, reason: reason.trim() };
      if (targetPeriod.trim()) body.target_period_label = targetPeriod.trim();
      const { data } = await api.post(`/admin/users/${member.user_code}/sp-adjust`, body);
      const detail = data.target_period_locked
        ? `Applied to open period ${data.applied_to_period} (adjusts locked ${data.adjusts_period}).`
        : `Applied to period ${data.applied_to_period}.`;
      toast.success(`SP ${direction === "add" ? "credited" : "debited"} · ${detail}`);
      onSaved?.(data);
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "SP adjustment failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-navy/40 backdrop-blur-sm p-4 overflow-y-auto" onClick={onClose}>
      <div
        onClick={(e) => e.stopPropagation()}
        data-testid="sp-adjust-modal"
        className="w-full max-w-2xl bg-white border border-border rounded-sm shadow-xl my-8"
      >
        <div className="flex items-start justify-between px-6 py-4 border-b border-border">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-navy/50">Edit SP (admin · super-admin)</div>
            <div className="text-navy text-lg mt-0.5">{member.user_code} · {member.name || "—"}</div>
            <div className="text-[11px] text-navy/50 mt-0.5">
              Current: self_sp <b className="text-olive">{member.self_sp || 0}</b>
              {" · "}left_sp <b>{member.left_sp || 0}</b>
              {" · "}right_sp <b>{member.right_sp || 0}</b>
              {" · "}total_sp <b>{member.total_sp || 0}</b>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button data-testid="sp-adjust-audit-toggle" onClick={loadAudit}
              className="inline-flex items-center gap-1 text-[11px] uppercase tracking-widest px-2 py-1 border border-navy/30 text-navy/70 rounded-sm hover:bg-navy/5">
              <History size={11} /> Audit
            </button>
            <button onClick={onClose} data-testid="sp-adjust-close" className="p-1.5 text-navy/50 hover:text-navy">
              <X size={16} />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-4">
          {/* Direction */}
          <div>
            <div className="text-[10px] uppercase tracking-widest text-navy/50 mb-1.5">Direction</div>
            <div className="grid grid-cols-2 gap-2">
              {[
                { k: "add",      l: "Add",      Icon: Plus,  cls: "text-olive border-olive/40" },
                { k: "subtract", l: "Subtract", Icon: Minus, cls: "text-rose border-rose/40" },
              ].map(({ k, l, Icon, cls }) => (
                <button key={k} type="button" onClick={() => setDirection(k)}
                  data-testid={`sp-adjust-dir-${k}`}
                  className={`inline-flex items-center justify-center gap-1 text-[11px] uppercase tracking-widest px-3 py-2 rounded-sm border ${direction === k ? `${cls} bg-white` : "border-line text-navy/50 bg-cream/30"}`}>
                  <Icon size={14} /> {l}
                </button>
              ))}
            </div>
          </div>

          {/* SP amount picker */}
          <div>
            <div className="text-[10px] uppercase tracking-widest text-navy/50 mb-1.5">SP amount</div>
            <div className="grid grid-cols-5 sm:grid-cols-9 gap-1.5">
              {SP_CHOICES.map((v) => (
                <button key={v} type="button" onClick={() => setAmountStr(String(v))}
                  data-testid={`sp-adjust-amount-${v}`}
                  className={`tabular-nums text-sm py-2 rounded-sm border ${amountOk && amount === v ? "bg-navy text-gold border-navy" : "border-line text-navy/70 hover:border-gold"}`}>
                  {direction === "add" ? "+" : "−"}{v}
                </button>
              ))}
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <input type="text" inputMode="decimal" value={amountStr}
                onChange={(e) => setAmountStr(e.target.value.replace(/[^0-9.]/g, ""))}
                placeholder="e.g. 15.5"
                data-testid="sp-adjust-amount-input"
                className={`w-28 border rounded-sm px-2 py-1.5 text-sm tabular-nums outline-none ${amountOk ? "border-line focus:border-royal" : "border-rose text-rose"}`} />
              <span className="text-[11px] text-navy/50">or type any amount {SP_MIN}–{SP_MAX} SP, 1 decimal (e.g. 15.5 or 24)</span>
            </div>
            {!amountOk && (
              <div className="text-[11px] text-rose mt-1" data-testid="sp-adjust-amount-error">
                Enter {SP_MIN}–{SP_MAX} SP with at most 1 decimal
              </div>
            )}
          </div>

          {/* Target period (optional) */}
          <label className="block">
            <span className="text-[10px] uppercase tracking-widest text-navy/50">
              Target period label <span className="normal-case text-navy/40 italic">(optional — leave blank for current open period)</span>
            </span>
            <input type="text" value={targetPeriod} onChange={(e) => setTargetPeriod(e.target.value)}
              placeholder="e.g. 05_sep_to_12_sep_2026"
              data-testid="sp-adjust-target-period"
              className="w-full mt-1 border border-line rounded-sm px-2 py-1.5 text-sm focus:border-royal outline-none font-mono" />
            <div className="text-[10px] text-navy/50 mt-1">
              If this period is already LOCKED, the correction will not rewrite it — it will be recorded against the current open period with a `adjusts_period` link back to this one.
            </div>
          </label>

          {/* Preview */}
          <div className="bg-cream/50 border border-line rounded-sm p-3">
            <div className="text-[10px] uppercase tracking-widest text-navy/50 mb-1.5">Proposed change</div>
            <div className="grid grid-cols-3 gap-2 text-sm">
              <div>
                <div className="text-[10px] text-navy/40 uppercase tracking-widest">Delta</div>
                <div className={`tabular-nums text-lg font-medium ${direction === "add" ? "text-olive" : "text-rose"}`} data-testid="sp-adjust-preview-delta">
                  {direction === "add" ? "+" : "−"}{amount} SP
                </div>
              </div>
              <div>
                <div className="text-[10px] text-navy/40 uppercase tracking-widest">Self SP: before → after</div>
                <div className="tabular-nums text-navy" data-testid="sp-adjust-preview-self">
                  {preview.before.self_sp} <span className="text-navy/40">→</span> <b>{preview.after.self_sp}</b>
                </div>
              </div>
              <div>
                <div className="text-[10px] text-navy/40 uppercase tracking-widest">Sponsor chain</div>
                <div className="text-navy/70 text-[11px]">
                  {direction === "add" ? "credited up-tree" : "debited up-tree"} + pair-match cascade
                </div>
              </div>
            </div>
          </div>

          <label className="block">
            <span className="text-[10px] uppercase tracking-widest text-navy/50">
              Reason (min 10 chars — permanently audit-logged)
            </span>
            <textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. SP recorded twice against wrong IBA on 12 Sept — reversing duplicate 50 SP"
              data-testid="sp-adjust-reason"
              className="w-full mt-1 border border-line rounded-sm px-3 py-2 text-sm focus:border-royal outline-none" />
          </label>

          {showAudit && (
            <div className="border border-line rounded-sm bg-cream/40 p-3 max-h-60 overflow-y-auto" data-testid="sp-adjust-audit-list">
              <div className="text-[10px] uppercase tracking-widest text-navy/50 mb-2">SP adjustment history</div>
              {audit.length === 0 && <div className="text-xs text-navy/50 italic">No previous adjustments.</div>}
              {audit.map((r) => (
                <div key={r.id} className="text-xs py-1.5 border-b border-line/50 last:border-0">
                  <div className="flex justify-between text-[10px] text-navy/50">
                    <span className={r.delta >= 0 ? "text-olive" : "text-rose"}>
                      {r.delta >= 0 ? "+" : ""}{r.delta} SP
                    </span>
                    <span>{new Date(r.created_at).toLocaleString()}</span>
                  </div>
                  <div className="text-navy/70 mt-0.5">
                    by <b>{r.actor_user_code}</b> → applied to <span className="font-mono">{r.applied_to_period}</span>
                    {r.adjusts_period && <> · adjusts <span className="font-mono text-rose">{r.adjusts_period}</span> <span className="uppercase text-[9px] px-1 py-0.5 rounded bg-rose/10 text-rose">locked</span></>}
                  </div>
                  {r.reason && <div className="text-navy/60 italic text-[11px] mt-0.5">"{r.reason}"</div>}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="px-6 py-3 border-t border-border flex items-center justify-between bg-cream/30">
          <div className="flex items-center gap-1.5 text-[10.5px] text-navy/50">
            <ShieldAlert size={11} className="text-gold-deep" />
            Locked closings preserved — corrections roll into the current open period.
          </div>
          <div className="flex items-center gap-2">
            <button onClick={onClose} data-testid="sp-adjust-cancel"
              className="text-[11px] uppercase tracking-widest px-3 py-2 border border-line text-navy/70 rounded-sm hover:bg-navy/5">
              Cancel
            </button>
            <button onClick={submit} disabled={busy || !amountOk} data-testid="sp-adjust-submit"
              className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest px-4 py-2 bg-royal text-white hover:bg-royal-deep rounded-sm disabled:opacity-50">
              {busy ? <RefreshCw size={12} className="animate-spin" /> : <Zap size={12} />}
              {busy ? "Applying…" : `${direction === "add" ? "Add" : "Subtract"} ${amount} SP`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
